/**
 * Rocks API
 *
 * API functions for teams, rocks, deliverables, comments, and linked items.
 * DMS document attachments live in `@/features/attachments`.
 */

import { api } from '@/lib/api';
import type {
  Rock,
  Deliverable,
  Comment,
  LinkedItem,
  Team,
  TeamMember,
  MastraTeamAssignment,
  CreateRockInput,
  UpdateRockInput,
  CreateDeliverableInput,
  RockFilters,
} from './types';

// =============================================================================
// Teams
// =============================================================================

export const getTeams = () => api.get<Team[]>('/v4/teams');

export const getTeamMembers = (teamId: string) =>
  api.get<TeamMember[]>(`/v4/teams/${teamId}/members`);

export const getAllTeamMembers = () =>
  api.get<TeamMember[]>('/v4/teams/all-members');

export const createTeam = (data: { name: string; type: string; is_private: boolean }) =>
  api.post<Team>('/v4/teams', data);

export const updateTeam = (id: string, data: { name?: string; type?: string; is_private?: boolean }) =>
  api.put<Team>(`/v4/teams/${id}`, data);

export const deleteTeam = (id: string) =>
  api.delete(`/v4/teams/${id}`);

export const addTeamMember = (teamId: string, userId: string, role: string) =>
  api.post<TeamMember>(`/v4/teams/${teamId}/members`, { user_id: userId, role });

export const removeTeamMember = (teamId: string, userId: string) =>
  api.delete(`/v4/teams/${teamId}/members/${userId}`);

export const updateTeamMemberRole = (teamId: string, userId: string, role: 'member' | 'leader') =>
  api.patch<TeamMember>(`/v4/teams/${teamId}/members/${userId}`, { role });

// =============================================================================
// Rocks
// =============================================================================

export const getRocks = (filters?: RockFilters) =>
  api.get<Rock[]>('/v4/rocks', filters);

export const getRock = (id: string) =>
  api.get<Rock>(`/v4/rocks/${id}`);

export const createRock = (data: CreateRockInput) =>
  api.post<Rock>('/v4/rocks', data);

export const updateRock = (id: string, data: UpdateRockInput) =>
  api.put<Rock>(`/v4/rocks/${id}`, data);

export const deleteRock = (id: string) =>
  api.delete<Rock>(`/v4/rocks/${id}`);

export const updateRockStatus = (id: string, status: string) =>
  api.patch<Rock>(`/v4/rocks/${id}/status`, { status });

export const archiveRock = (id: string) =>
  api.post<Rock>(`/v4/rocks/${id}/archive`, {});

export const unarchiveRock = (id: string) =>
  api.post<Rock>(`/v4/rocks/${id}/unarchive`, {});

export const reorderRock = (id: string, data: { sort_order?: number; board_column?: string }) =>
  api.patch<Rock>(`/v4/rocks/${id}/reorder`, data);

// =============================================================================
// Deliverables
// =============================================================================

export const createDeliverable = (rockId: string, data: CreateDeliverableInput) =>
  api.post<Deliverable>(`/v4/rocks/${rockId}/deliverables`, data);

export const updateDeliverable = (
  id: string,
  data: Partial<CreateDeliverableInput> & { completed?: boolean }
) => api.put<Deliverable>(`/v4/rocks/deliverables/${id}`, data);

export const deleteDeliverable = (id: string) =>
  api.delete(`/v4/rocks/deliverables/${id}`);

export const toggleDeliverable = (id: string) =>
  api.patch<Deliverable>(`/v4/rocks/deliverables/${id}/toggle`, {});

// =============================================================================
// Comments
// =============================================================================

export const getComments = (entityType: string, entityId: string) =>
  api.get<Comment[]>('/v4/rocks/comments', { entity_type: entityType, entity_id: entityId });

export const createComment = (data: {
  entity_type: string;
  entity_id: string;
  body: string;
}) => api.post<Comment>('/v4/rocks/comments', data);

// =============================================================================
// Linked Items
// =============================================================================

export const getLinkedItems = (sourceType: string, sourceId: string) =>
  api.get<LinkedItem[]>('/v4/rocks/linked-items', {
    source_type: sourceType,
    source_id: sourceId,
  });

export const createLinkedItem = (data: {
  source_type: string;
  source_id: string;
  target_type: string;
  target_id: string;
}) => api.post<LinkedItem>('/v4/rocks/linked-items', data);

export const deleteLinkedItem = (id: string) =>
  api.delete(`/v4/rocks/linked-items/${id}`);

// =============================================================================
// Employees (for owner assignment across EOS features: KPIs, Rocks, To-Dos)
// =============================================================================

interface EmployeeListItem {
  id: string;
  mongo_user_id: string | null;
  email: string | null;
  firstname: string | null;
  lastname: string | null;
  enabled: boolean;
  profile_photo_url?: string | null;
}

interface EmployeesResponse {
  employees: EmployeeListItem[];
}

export interface OwnerOption {
  id: string;
  mongoUserId: string | null;
  name: string;
  email: string;
  /** Public URL to the employee's profile photo, if any. */
  profilePhotoUrl: string | null;
}

export async function fetchOwnerOptions(): Promise<OwnerOption[]> {
  const response = await api.get<EmployeesResponse>('/v4/employees');

  return (response.employees || [])
    .filter((e) => e.enabled)
    .map((e) => ({
      id: e.id, // Postgres users.id (UUID) from the Employees directory
      mongoUserId: e.mongo_user_id ?? null,
      name: `${e.firstname || ''} ${e.lastname || ''}`.trim() || (e.email ?? ''),
      email: e.email ?? '',
      profilePhotoUrl: e.profile_photo_url ?? null,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// =============================================================================
// Mastra Team Assignments
// =============================================================================

export const getMastraAssignments = (params?: { entity_type?: string; team_id?: string }) =>
  api.get<MastraTeamAssignment[]>('/v4/teams/mastra-assignments', params);

export const upsertMastraAssignment = (entityType: string, entityId: string, teamId: string) =>
  api.put<MastraTeamAssignment>(`/v4/teams/mastra-assignments/${entityType}/${entityId}`, { team_id: teamId });

export const deleteMastraAssignment = (entityType: string, entityId: string) =>
  api.delete(`/v4/teams/mastra-assignments/${entityType}/${entityId}`);
